import { useEffect } from 'react';
import { useRouteMatch, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { PATHS } from 'src/common/constants';
import {
  useSuiteAPI,
  UseSuiteAPIReturn,
} from 'src/SharedCustomHooks/UseSuiteAPI/useSuiteAPI';
import {
  usePropertiesAPI,
  UsePropertiesAPIReturn,
} from 'src/SharedCustomHooks/UsePropertiesAPI/usePropertiesAPI';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { SidebarContextRow } from '../Components/SidebarContextTree';
import {
  assetRow,
  keyPickupRow,
  parseSuiteRouteId,
  suiteRow,
} from './sidebarContextRows.helpers';

interface RouteParams {
  id: string;
}

interface RouteMatch<Params> {
  path: string;
  url: string;
  isExact: boolean;
  params: Params;
}

interface AssetRouteState {
  asset?: any;
  suite?: any;
  property?: any;
}

const propertyRow = (
  propertyId: string,
  propertyName: string | null
): SidebarContextRow => ({
  label: propertyName ?? '',
  path: PATHS.PROPERTIES_VIEW.replace(':id', propertyId),
  icon: 'building',
  loading: !propertyName,
});

/* TODO(sidebar-asset-fetch): the URL has no suite/property and there's no single-asset GET, so
 * the chain is built from router state — a hard refresh loses it and renders nothing. Fetch the
 * asset + parents from an endpoint when one exists so it survives refresh. */
const buildAssetRows = (
  assetRouteId: string,
  state: AssetRouteState | undefined
): SidebarContextRow[] | null => {
  if (!state?.asset || !state?.suite || !state?.property) {
    return null;
  }

  const { asset, suite, property }: AssetRouteState = state;

  return [
    propertyRow(property.id, property.name ?? null),
    suiteRow(`${property.id}&${suite.id}`, suite.number ?? null),
    assetRow(assetRouteId, asset.item?.name ?? null),
  ];
};

const usePropertyContextRows = (): SidebarContextRow[] | null => {
  // Use React Router to determine which route is the sidebar rendering context for?.
  const assetRouteMatch: RouteMatch<RouteParams> | null =
    useRouteMatch<RouteParams>({ path: PATHS.ASSETS_VIEW });

  const keyPickupRouteMatch: RouteMatch<RouteParams> | null =
    useRouteMatch<RouteParams>({ path: PATHS.KEY_PICKUP_VIEW });

  const suiteRouteMatch: RouteMatch<RouteParams> | null =
    useRouteMatch<RouteParams>({ path: PATHS.SUITES_VIEW });

  const propertyRouteMatch: RouteMatch<RouteParams> | null =
    useRouteMatch<RouteParams>({
      path: PATHS.PROPERTIES_VIEW,
      exact: true,
    });

  const location = useLocation<AssetRouteState>();
  const user: any = useSelector(selectAuthenticatedUser);

  const onSuiteRoute: boolean = suiteRouteMatch !== null;
  const onKeyPickupRoute: boolean = keyPickupRouteMatch !== null;

  const isSuiteScopedRoute: boolean = onSuiteRoute || onKeyPickupRoute;

  const rawSuiteId: string =
    suiteRouteMatch?.params?.id || keyPickupRouteMatch?.params?.id || '';

  const { suiteId, propertyId: suitePropertyId } =
    parseSuiteRouteId(rawSuiteId);

  const propertyId: string = isSuiteScopedRoute
    ? suitePropertyId
    : propertyRouteMatch?.params?.id || '';

  const { suite, fetchSuiteById }: UseSuiteAPIReturn = useSuiteAPI();

  const { properties, fetchProperties }: UsePropertiesAPIReturn =
    usePropertiesAPI();

  useEffect(() => {
    if (!suiteId) {
      return;
    }

    fetchSuiteById(suiteId);
  }, [suiteId]);

  useEffect(() => {
    if (!propertyId || !user?.id) {
      return;
    }

    fetchProperties(user.id, user.roles[0].role.name);
  }, [propertyId, user?.id]);

  /* 4: Derive the property label. TODO(API): suite payloads return `propertyName: null`, so look the name up
   * from the user's properties by id. Once the suite API populates it, drop the properties
   * fetch + find and read `suite.propertyName` directly. */
  const property: any = properties.find(
    (entry: any) => entry.id === propertyId
  );

  const propertyName: string | null =
    property?.name || suite?.propertyName || null;

  const propertyContextRow: SidebarContextRow = propertyRow(
    propertyId,
    propertyName
  );

  const suiteContextRow: SidebarContextRow = suiteRow(
    rawSuiteId,
    suite?.number || null
  );

  if (assetRouteMatch) {
    return buildAssetRows(assetRouteMatch.params.id, location.state);
  }

  if (keyPickupRouteMatch) {
    return [propertyContextRow, suiteContextRow, keyPickupRow(rawSuiteId)];
  }

  if (suiteRouteMatch) {
    return [propertyContextRow, suiteContextRow];
  }

  if (propertyRouteMatch) {
    return [propertyContextRow];
  }

  return null;
};

export default usePropertyContextRows;
